/* eslint-disable react/button-has-type */
import React, { useRef } from 'react';

import { useQuery } from '@tanstack/react-query';

import NoData from '../../../../NoData';
import BorrowerForm from './BorrowerForm';
import SearchForm from '../../../SearchForm';
import Button from '../../../../Button';

import LoanService from '../../../../../services/LoanService';
import BorrowerService from '../../../../../services/BorrowerService';

function compareLoanPosition(a, b) {
  if (a.position === 'primary' && b.position === 'secondary') {
    return -1;
  }
  if (a.position === 'secondary' && b.position === 'primary') {
    return 1;
  }
  return 0;
}

export default function Borrowers({
  borrowers,
  setBorrowers,
  loanId,
  loan,
  switchToNextSection,
  isVisible,
  hasBorrowers,
  readOnly,
  refetchLoan,
}) {
  const childRefs = useRef({});

  const { data: borrowersData, isFetching: areBorrowersFetching } = useQuery({
    queryKey: ['borrowers', loanId],
    queryFn: () => LoanService.getLoanBorrowers(loanId),
    enabled: !!readOnly,
  });

  let currentBorrowers = borrowers;
  if (readOnly && borrowersData) {
    currentBorrowers = borrowersData;
  }

  const areBorrowersExist = currentBorrowers?.length > 0;

  return (
    <div className="flex flex-col gap-4 h-full">
      {!readOnly && (
        <div className="flex gap-4 flex-col md:flex-row justify-end">
          {areBorrowersExist && (
            <Button
              className="md:!max-w-[200px]"
              onClick={() => {
                setBorrowers((prevBorrowers) => [
                  { borrower_id: Math.random() },
                  ...prevBorrowers,
                ]);
              }}
            >
              <svg
                className="w-4 h-4 fill-current opacity-50 shrink-0"
                viewBox="0 0 16 16"
              >
                <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
              </svg>
              <span className="ml-2">
                {areBorrowersExist ? 'Create Coborrower' : 'Create Borrower'}
              </span>
            </Button>
          )}
          <SearchForm
            queryFn={BorrowerService.searchBorrowers}
            queryKey={['borrowers', loan?.lender_id]}
            queryFnParams={{ lenderId: loan?.lender_id }}
            onResultSelect={(result) => {
              const id = Math.random().toString();
              setBorrowers((prevBorrowers) => [
                { id, borrowerFromSearch: result },
                ...prevBorrowers,
              ]);

              // Wait for the form to be rendered and then submit it
              const timer = setInterval(() => {
                if (childRefs.current[id]) {
                  childRefs.current[id].submitForm();
                  clearInterval(timer);
                }
              }, 100);
            }}
          />
        </div>
      )}
      <div className="flex-grow flex flex-col gap-10 overflow-auto">
        {currentBorrowers.sort(compareLoanPosition).map((borrower, index) => {
          return (
            <BorrowerForm
              index={index}
              loanId={loanId}
              hasBorrowers={hasBorrowers}
              key={borrower.borrower_id}
              borrower={borrower}
              borrowers={currentBorrowers}
              setBorrowers={setBorrowers}
              noSubmit
              loan={loan}
              switchToNextSection={switchToNextSection}
              isVisible={isVisible}
              readOnly={readOnly}
              refetchLoan={refetchLoan}
              id={borrower.id || borrower.borrower_id}
              // eslint-disable-next-line no-return-assign
              ref={(el) =>
                (childRefs.current[borrower.id || borrower.borrower_id] = el)
              }
            />
          );
        })}
        {!areBorrowersFetching && !areBorrowersExist && (
          <div>
            <NoData
              buttonLabel="Create Borrower"
              title="No borrowers have been added to this loan yet"
              onButtonClick={() =>
                setBorrowers((prevBorrowers) => [
                  { borrower_id: Math.random() },
                  ...prevBorrowers,
                ])
              }
              hideButton={readOnly}
            />
          </div>
        )}
      </div>
    </div>
  );
}
